import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFinancial } from '../context/FinancialContext';
import { calculateGoalMetrics, calculateRetirementMetrics } from '../utils/calculations';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GoalCard from './GoalCard';
import GoalForm from './GoalForm';
import RetirementGoalCard from './RetirementGoalCard';
import RetirementForm from './RetirementForm';

const { FiTarget, FiPlus, FiTrendingUp, FiPieChart } = FiIcons;

function GoalEntry() {
  const { state, dispatch } = useFinancial();
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showRetirementForm, setShowRetirementForm] = useState(false);

  const handleAddGoal = (goalData) => {
    const metrics = calculateGoalMetrics(goalData, state.userProfile);
    const goalWithMetrics = { ...goalData, ...metrics };

    if (editingGoal) {
      dispatch({ type: 'UPDATE_GOAL', payload: { ...goalWithMetrics, id: editingGoal.id } });
      setEditingGoal(null);
    } else {
      dispatch({ type: 'ADD_GOAL', payload: goalWithMetrics });
    }
    setShowForm(false);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setShowForm(true);
  };

  const handleDeleteGoal = (goalId) => {
    dispatch({ type: 'DELETE_GOAL', payload: goalId });
  };

  const handleUpdateRetirementPlan = (retirementData) => {
    dispatch({ type: 'UPDATE_RETIREMENT_PLAN', payload: retirementData });
    setShowRetirementForm(false);
  };

  const canAddMoreGoals = state.goals.length < 10;

  // Calculate retirement metrics
  const retirementMetrics = calculateRetirementMetrics(state.retirementPlan, state.userProfile);

  // Calculate total SIP needed across all goals and retirement
  const totalGoalsSIP = state.goals.reduce((sum, goal) => sum + (goal.requiredSIP || 0), 0);
  const totalSIP = totalGoalsSIP + (retirementMetrics.requiredSIP || 0);

  // Total goals count INCLUDING retirement
  const totalGoalsCount = state.goals.length + 1; // +1 for retirement

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto"
    >
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-[#19b48d] flex items-center justify-center">
            <SafeIcon icon={FiTarget} className="text-white text-lg" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Financial Goals</h2>
            <p className="text-gray-600">Define up to 10 financial goals to track and achieve (Retirement included)</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {canAddMoreGoals && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-[#19b48d] text-white rounded-lg hover:bg-[#16a085] transition-colors duration-200"
            >
              <SafeIcon icon={FiPlus} className="text-sm" />
              <span>Add Goal</span>
            </button>
          )}
        </div>
      </div>

      {/* Goals Overview - Updated to include retirement */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#19b48d]/10 flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="text-[#19b48d] text-sm" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Goals (incl. Retirement)</p>
              <p className="text-2xl font-bold text-gray-900">{totalGoalsCount}/11</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#f58320]/10 flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-[#f58320] text-sm" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Monthly SIP</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{totalSIP.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="text-green-600 text-sm" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Goals on Track</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.goals.filter(goal => goal.achievementProbability >= 90).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Retirement Goal Card */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiPieChart} className="text-[#19b48d] text-lg" />
            <h3 className="text-lg font-semibold text-gray-900">Retirement Goal</h3>
          </div>
          <button
            onClick={() => setShowRetirementForm(true)}
            className="flex items-center space-x-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-sm"
          >
            <SafeIcon icon={FiPlus} className="text-xs" />
            <span>Update Retirement Plan</span>
          </button>
        </div>
        <RetirementGoalCard
          retirementPlan={state.retirementPlan}
          userProfile={state.userProfile}
          retirementMetrics={retirementMetrics}
          onEdit={() => setShowRetirementForm(true)}
        />
      </div>

      {/* Goals List */}
      <div className="space-y-6">
        <AnimatePresence>
          {state.goals.map((goal, index) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              index={index}
              userProfile={state.userProfile}
              onEdit={handleEditGoal}
              onDelete={handleDeleteGoal}
            />
          ))}
        </AnimatePresence>

        {state.goals.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiTarget} className="text-gray-400 text-2xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No goals yet</h3>
            <p className="text-gray-600 mb-6">Start by adding your first financial goal</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-[#19b48d] text-white rounded-lg hover:bg-[#16a085] transition-colors duration-200"
            >
              Add Your First Goal
            </button>
          </motion.div>
        )}
      </div>

      {/* Goal Form Modal */}
      <AnimatePresence>
        {showForm && (
          <GoalForm
            goal={editingGoal}
            userProfile={state.userProfile}
            onSubmit={handleAddGoal}
            onClose={() => {
              setShowForm(false);
              setEditingGoal(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Retirement Form Modal */}
      <AnimatePresence>
        {showRetirementForm && (
          <RetirementForm
            retirementPlan={state.retirementPlan}
            userProfile={state.userProfile}
            onSubmit={handleUpdateRetirementPlan}
            onClose={() => setShowRetirementForm(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default GoalEntry;